/**
 * useAudioChat — web port of `apps/WunderGuide/hooks/useAudioChat.ts`.
 *
 * Real LiveKit integration matching RN:
 *   1. Lazily bootstrap a chat session (GET /functions/v1/chat/sessions; if
 *      empty, look up an agent via /functions/v1/locations and create one).
 *   2. POST /sessions/v1/{sessionId}/start with the same payload RN sends to
 *      get `{ token, lkUrl, serverHints }`.
 *   3. Connect a LiveKit `Room` to that URL, publish the local mic track,
 *      subscribe to remote audio.
 *   4. Surface mic + bot audio levels via Web Audio analysers so the
 *      MicButton ripples respond to actual speech and the bot's voice.
 *   5. Surface `hasRemoteParticipant` only when an actual agent joins, so
 *      MicButton's `isReadyToEnd` flips to red X correctly.
 *
 * Failure modes (no chat session, REST 401, LiveKit connect fail, etc.)
 * fall back to listen-only mic so the UI still feels alive.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Room,
  RoomEvent,
  Track,
  RemoteTrack,
  RemoteAudioTrack,
  RemoteParticipant,
  RemoteTrackPublication,
  ConnectionState,
  createAudioAnalyser,
} from 'livekit-client';
import { httpClient } from '../http';
import logger from '../utils/logger';
import { useChatSession } from '../context/ChatSessionContext';

export interface UseAudioChat {
  isListening: boolean;
  isConnecting: boolean;
  hasRemoteParticipant: boolean;
  micAudioLevel: number;
  botAudioLevel: number;
  isBotWorking: boolean;
  permissionState: 'unknown' | 'prompt' | 'granted' | 'denied';
  /**
   * Server-driven AI disclaimer (from serverHints.disclaimer in the REST
   * /start response). Falls back to the bundled DISCLAIMER translation
   * in the consumer; matches RN AudioChat.tsx Disclaimer component.
   */
  disclaimer?: string;
  toggleListening: () => Promise<void>;
  startListening: () => Promise<void>;
  stopListening: () => void;
  /**
   * Publish/unpublish the browser camera into the active agent room for the
   * realtime Live Vision (Gemini Live) path. Returns the local camera track
   * (for preview via `track.attach()`) when enabling, or null. No-op (null)
   * when there is no connected room.
   */
  setLiveVideoEnabled: (enabled: boolean) => Promise<any | null>;
}

interface SessionStartResponse {
  token: string;
  lkUrl: string;
  room: string;
  expiresAt?: number;
  serverHints?: Record<string, unknown>;
}

interface ChatSessionDTO {
  id: string;
  agent_id?: string;
  agentId?: string;
}

const CHAT_API = '/functions/v1/chat';
const LOCATION_API = '/functions/v1/locations';
const DEFAULT_DESTINATION_SEARCH = 'Hawaii';

function computeRms(samples: Float32Array): number {
  let sum = 0;
  for (let i = 0; i < samples.length; i++) {
    const v = samples[i];
    sum += v * v;
  }
  return Math.sqrt(sum / samples.length);
}

export function useAudioChat(): UseAudioChat {
  const [isListening, setIsListening] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [hasRemoteParticipant, setHasRemoteParticipant] = useState(false);
  const [micAudioLevel, setMicAudioLevel] = useState(0);
  const [botAudioLevel, setBotAudioLevel] = useState(0);
  const [isBotWorking, setIsBotWorking] = useState(false);
  const [permissionState, setPermissionState] =
    useState<UseAudioChat['permissionState']>('unknown');
  const [disclaimer, setDisclaimer] = useState<string | undefined>(undefined);

  // The agent switcher writes the active session here; the voice binds to it so
  // switching guides re-points the next activation to the chosen agent.
  const { selectedSessionId } = useChatSession();
  const selectedSessionIdRef = useRef<string | null>(selectedSessionId);

  const roomRef = useRef<Room | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  // Per-mic-activation fencing token (mirrors RN). A fresh value each /start
  // makes the server treat this tap as the current activation generation, so
  // exactly one agent (the latest tap's) stays in the room.
  const activationIdRef = useRef<string | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const micAnalyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);
  // Hidden <audio> elements we appended in TrackSubscribed. We track them
  // explicitly because relying on TrackUnsubscribed for cleanup is brittle:
  // when tearDown calls `room.disconnect()` and immediately nulls the ref,
  // the per-track unsubscribe events fire on a Room that's already being
  // collected and our handlers may never run. Without explicit cleanup the
  // elements pile up on document.body across activations; on a warm
  // reactivation the new track may attach to a fresh element while
  // hidden-stale elements still try to play, contributing to "audio track
  // arrives but no frames" behavior the harness caught.
  const audioElsRef = useRef<HTMLAudioElement[]>([]);

  const tearDown = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    micAnalyserRef.current?.disconnect();
    micAnalyserRef.current = null;
    for (const a of botAnalysersRef.current.values()) {
      a.cleanup().catch(() => {});
    }
    botAnalysersRef.current.clear();
    // Explicitly remove every hidden audio element we created for remote
    // tracks. See `audioElsRef` comment above.
    for (const el of audioElsRef.current) {
      try {
        el.pause();
        (el as any).srcObject = null;
        el.remove();
      } catch {}
    }
    audioElsRef.current = [];
    // Close the AudioContext on a *new* microtask so any in-flight
    // analyser tick can drain before we tear it down. Without this, the
    // RAF tick that's already scheduled hits a half-closed context and
    // logs noisy "AudioContext was not allowed to start" warnings on the
    // next session.
    const ctx = audioCtxRef.current;
    audioCtxRef.current = null;
    if (ctx) {
      Promise.resolve().then(() => ctx.close().catch(() => {}));
    }
    if (localStreamRef.current) {
      for (const t of localStreamRef.current.getTracks()) t.stop();
      localStreamRef.current = null;
    }
    if (roomRef.current) {
      const room = roomRef.current;
      roomRef.current = null;
      // disconnect() fires TrackUnsubscribed for each subscribed track
      // before resolving. We already cleaned up <audio> elements above,
      // so this is just to let the SFU release the publish slot. Don't
      // race the next connect — wait inside a microtask.
      Promise.resolve().then(() => room.disconnect().catch(() => {}));
    }
    // CRITICAL: clear the cached session id. Without this,
    // bootstrapSession's early-return at line 124-126 reuses a stale
    // session_id on the second activation; the server-side session has
    // already been released by the prior agent worker, but the client
    // sends `/sessions/v1/{stale-sid}/start` and ends up bound to a
    // freshly dispatched agent with mismatched WebRTC state. Result:
    // agent audio track arrives but no audio frames flow — exactly the
    // warm-reactivation failure mode the e2e harness reproduces 4/5.
    sessionIdRef.current = null;
    activationIdRef.current = null;
    setHasRemoteParticipant(false);
    setMicAudioLevel(0);
    setBotAudioLevel(0);
    setIsBotWorking(false);
    setDisclaimer(undefined);
  }, []);

  useEffect(() => () => tearDown(), [tearDown]);

  // ── Bootstrap chat session (find or create) ─────────────────────────
  const bootstrapSession =
    useCallback(async (): Promise<ChatSessionDTO | null> => {
      if (sessionIdRef.current) {
        return { id: sessionIdRef.current };
      }
      // Prefer an explicitly-selected session (agent switcher / hydration) so
      // the voice binds to the guide the user chose rather than "first session".
      if (selectedSessionIdRef.current) {
        sessionIdRef.current = selectedSessionIdRef.current;
        return { id: selectedSessionIdRef.current };
      }
      try {
        // Try existing session list first.
        const list = await httpClient.get<
          { sessions?: ChatSessionDTO[] } | ChatSessionDTO[]
        >(`${CHAT_API}/sessions`);
        const arr = Array.isArray(list.data)
          ? list.data
          : (list.data?.sessions ?? []);
        if (arr.length > 0) {
          sessionIdRef.current = arr[0].id;
          return arr[0];
        }
      } catch (err) {
        logger.warn('[AudioChat] list sessions failed', { err });
      }

      // No sessions → look up an agent for our default destination and create one.
      try {
        const loc = await httpClient.get<{
          agents?: { id: string; name?: string }[];
        }>(LOCATION_API, { params: { search: DEFAULT_DESTINATION_SEARCH } });
        const agent = loc.data?.agents?.[0];
        if (!agent?.id) {
          logger.warn('[AudioChat] no default agent available');
          return null;
        }
        const created = await httpClient.post<
          { session?: ChatSessionDTO } & ChatSessionDTO
        >(`${CHAT_API}/sessions`, {
          title: 'Voice session',
          agentId: agent.id,
        });
        const session = (created.data as any)?.session ?? created.data;
        if (!session?.id) return null;
        sessionIdRef.current = session.id;
        return session;
      } catch (err) {
        logger.warn('[AudioChat] create session failed', { err });
        return null;
      }
    }, []);

  // ── REST session start → LiveKit creds ──────────────────────────────
  const restStartSession = useCallback(
    async (
      sessionId: string,
      agentId?: string,
    ): Promise<SessionStartResponse | null> => {
      try {
        // New per-tap fencing token: `${sessionId}_${ms}_${nonce}` (same shape
        // as the RN client). The server parses the embedded ms for tap order.
        const activationId = `${sessionId}_${Date.now()}_${Math.random()
          .toString(36)
          .slice(2, 8)}`;
        activationIdRef.current = activationId;
        const { data } = await httpClient.post<SessionStartResponse>(
          `/sessions/v1/${sessionId}/start`,
          {
            regionHint: null,
            lang: navigator.language || 'en-US',
            e2ee: null,
            capabilities: { voice: true },
            agentId,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            destinationLocation: null,
            activationId,
          },
        );
        // Surface the server-driven AI disclaimer so the UI can render it
        // exactly like the RN AudioChat overlay. Same pattern as
        // apps/WunderGuide/hooks/useAudioChat.ts ("Store server hints
        // (disclaimer, etc.) from REST response").
        const hint = (data?.serverHints as { disclaimer?: unknown } | undefined)
          ?.disclaimer;
        if (typeof hint === 'string' && hint.trim()) setDisclaimer(hint);
        return data;
      } catch (err) {
        logger.warn('[AudioChat] /sessions/v1/start failed', { err });
        return null;
      }
    },
    [],
  );

  // ── Mic permission + analyser pump ──────────────────────────────────
  const acquireMic = useCallback(async (): Promise<MediaStream | null> => {
    if (
      typeof navigator === 'undefined' ||
      !navigator.mediaDevices?.getUserMedia
    ) {
      setPermissionState('denied');
      return null;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      });
      setPermissionState('granted');
      localStreamRef.current = stream;
      return stream;
    } catch (err: any) {
      const denied =
        err?.name === 'NotAllowedError' ||
        err?.name === 'PermissionDeniedError';
      setPermissionState(denied ? 'denied' : 'prompt');
      return null;
    }
  }, []);

  const ensureAudioContext = useCallback((): AudioContext | null => {
    if (audioCtxRef.current) return audioCtxRef.current;
    const Ctor =
      (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Ctor) return null;
    const ctx: AudioContext = new Ctor();
    audioCtxRef.current = ctx;
    // iOS Safari starts AudioContexts in `suspended` state until a user
    // gesture *and* an explicit resume(). Without this, analysers read
    // silence forever and MicButton never shows ripples on iPhone.
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
    return ctx;
  }, []);

  const attachMicAnalyser = useCallback(
    (stream: MediaStream) => {
      const ctx = ensureAudioContext();
      if (!ctx) return;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.85;
      src.connect(analyser);
      micAnalyserRef.current = analyser;
    },
    [ensureAudioContext],
  );

  // Bot level: per-track Web Audio analyser via LK's `createAudioAnalyser`
  // helper. Why not `participant.audioLevel`?  The agent publishes TWO
  // tracks per session (microphone for the welcome TTS, unknown for the
  // realtime LLM voice), and the SFU only computes audio levels for
  // microphone-source tracks — so `participant.audioLevel` is silent for
  // the LLM voice. Tapping each track individually catches both.
  //
  // `createAudioAnalyser` is the same primitive `@livekit/components-react`
  // uses under the hood for `useTrackVolume`; it works on every browser
  // because it taps the live mediaStreamTrack, not a cloned MediaStream
  // (which doesn't pull samples) or `<audio srcObject>` (silent in iOS).
  type BotAnalyser = {
    calculateVolume: () => number;
    cleanup: () => Promise<void>;
  };
  const botAnalysersRef = useRef(new Map<string, BotAnalyser>());

  const attachBotAnalyser = useCallback((track: RemoteTrack) => {
    if (track.kind !== Track.Kind.Audio) return;
    const sid = track.sid;
    if (!sid || botAnalysersRef.current.has(sid)) return;
    try {
      const { calculateVolume, cleanup } = createAudioAnalyser(
        track as RemoteAudioTrack,
        { cloneTrack: false, smoothingTimeConstant: 0.6 },
      );
      botAnalysersRef.current.set(sid, { calculateVolume, cleanup });
    } catch (err) {
      logger.warn('[AudioChat] createAudioAnalyser failed', { err });
    }
  }, []);

  const detachBotAnalyser = useCallback((track: RemoteTrack) => {
    const sid = track.sid;
    if (!sid) return;
    const handle = botAnalysersRef.current.get(sid);
    if (!handle) return;
    botAnalysersRef.current.delete(sid);
    handle.cleanup().catch(() => {});
  }, []);

  const startAnalyserLoop = useCallback(() => {
    const micBuf = new Float32Array(1024);
    const tick = () => {
      // Mic — keep the Web Audio analyser. It works because getUserMedia
      // streams flow into Web Audio fine on every browser. Could swap to
      // `localParticipant.audioLevel` for full RN parity, but no reason
      // to since the analyser is already proven to work here.
      if (micAnalyserRef.current) {
        micAnalyserRef.current.getFloatTimeDomainData(micBuf);
        setMicAudioLevel(Math.min(1, computeRms(micBuf) * 4));
      }
      // Bot — read max volume across every per-track analyser we've
      // attached. Bumped *2 since `calculateVolume` returns RMS over byte
      // FFT data which tends to read smaller than the rough envelope the
      // ripple threshold (0.03) was tuned for.
      let botLvl = 0;
      for (const a of botAnalysersRef.current.values()) {
        const v = a.calculateVolume();
        if (v > botLvl) botLvl = v;
      }
      botLvl = Math.min(1, botLvl * 2);
      setBotAudioLevel(botLvl);
      setIsBotWorking(botLvl > 0.05);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  // ── Connect LiveKit room ────────────────────────────────────────────
  const connectLiveKit = useCallback(
    async (creds: SessionStartResponse, micStream: MediaStream) => {
      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
        publishDefaults: { dtx: true, red: true },
      });
      roomRef.current = room;

      room.on(RoomEvent.ParticipantConnected, (p: RemoteParticipant) => {
        logger.info('[AudioChat] remote joined', { identity: p.identity });
        setHasRemoteParticipant(true);
      });
      room.on(RoomEvent.ParticipantDisconnected, () => {
        setHasRemoteParticipant(
          (roomRef.current?.remoteParticipants?.size ?? 0) > 0,
        );
      });
      room.on(
        RoomEvent.TrackSubscribed,
        (
          track: RemoteTrack,
          _pub: RemoteTrackPublication,
          _p: RemoteParticipant,
        ) => {
          if (track.kind === Track.Kind.Audio) {
            // Auto-play remote audio in a hidden element AND wire a
            // per-track Web Audio analyser so we can pulse the orb on the
            // unknown-source LLM voice (SFU doesn't speaker-track those).
            const el = track.attach() as HTMLAudioElement;
            el.style.display = 'none';
            document.body.appendChild(el);
            audioElsRef.current.push(el);
            attachBotAnalyser(track);
          }
        },
      );
      room.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
        for (const el of track.detach()) {
          try {
            (el as any).srcObject = null;
          } catch {}
          el.remove();
          // Drop from our tracked list so tearDown doesn't double-remove
          const idx = audioElsRef.current.indexOf(el as HTMLAudioElement);
          if (idx >= 0) audioElsRef.current.splice(idx, 1);
        }
        detachBotAnalyser(track);
      });
      room.on(RoomEvent.ConnectionStateChanged, (state: ConnectionState) => {
        logger.debug('[AudioChat] LK state', { state });
      });

      await room.connect(creds.lkUrl, creds.token);

      // Publish the existing mic stream (don't re-prompt)
      const [audioTrack] = micStream.getAudioTracks();
      if (audioTrack) {
        await room.localParticipant.publishTrack(audioTrack, {
          name: 'microphone',
          source: Track.Source.Microphone,
        });
      }

      // Reflect any participants already in the room (the agent may have joined first)
      if ((room.remoteParticipants?.size ?? 0) > 0) {
        setHasRemoteParticipant(true);
      }

      // NOTE: First-activation chime is fired by the agent's watchdog
      // (worker.py activation_watchdog) when it detects this participant.
      // Subsequent activations within the same agent job reconnect to the
      // existing worker silently (no new chime). Tried both performRpc
      // (mic_activate method) and publishData (mic_activate JSON payload)
      // to re-trigger chimes — neither reaches the Python SDK 1.5.9
      // agent's handlers from the JS SDK 2.17.2 client in this stack.
      // RN's matching path uses RPC and works there; the JS SDK's RPC
      // is the suspected interop gap. Tracked as follow-up; for now,
      // first connect chimes, subsequent reactivations are silent.
    },
    [attachBotAnalyser, detachBotAnalyser],
  );

  // ── Public: start / stop / toggle ───────────────────────────────────
  const startListening = useCallback(async () => {
    if (isListening || isConnecting) return;
    setIsConnecting(true);
    // CRITICAL: create + resume the AudioContext synchronously inside the
    // user-gesture handler. If we wait until after `await acquireMic()`,
    // iOS Safari has already closed the gesture window and the context
    // stays `suspended` — analyser reads zero forever and MicButton shows
    // no ripples. Doing it first means the gesture flows straight into
    // resume() before any await.
    ensureAudioContext();
    try {
      const micStream = await acquireMic();
      if (!micStream) {
        setIsConnecting(false);
        return;
      }
      attachMicAnalyser(micStream);
      startAnalyserLoop();

      // Bootstrap session + REST start in parallel where possible
      const session = await bootstrapSession();
      if (!session) {
        // Bail with mic-only mode
        setIsConnecting(false);
        setIsListening(true);
        return;
      }

      const creds = await restStartSession(
        session.id,
        (session as any).agentId ?? (session as any).agent_id,
      );
      if (!creds) {
        setIsConnecting(false);
        setIsListening(true);
        return;
      }

      await connectLiveKit(creds, micStream);
      setIsConnecting(false);
      setIsListening(true);
    } catch (err) {
      logger.warn('[AudioChat] startListening failed', { err });
      setIsConnecting(false);
      setIsListening(false);
    }
  }, [
    isListening,
    isConnecting,
    acquireMic,
    attachMicAnalyser,
    startAnalyserLoop,
    bootstrapSession,
    restStartSession,
    connectLiveKit,
  ]);

  const stopListening = useCallback(() => {
    setIsListening(false);
    setIsConnecting(false);
    tearDown();
  }, [tearDown]);

  const toggleListening = useCallback(async () => {
    if (isListening || isConnecting) {
      stopListening();
    } else {
      await startListening();
    }
  }, [isListening, isConnecting, startListening, stopListening]);

  const setLiveVideoEnabled = useCallback(async (enabled: boolean) => {
    const room = roomRef.current;
    const lp = room?.localParticipant;
    if (!lp) return null;
    try {
      await lp.setCameraEnabled(enabled);
      if (!enabled) return null;
      const pub = lp.getTrackPublication(Track.Source.Camera);
      return (pub as any)?.videoTrack ?? (pub as any)?.track ?? null;
    } catch {
      return null;
    }
  }, []);

  // Agent switch: when the selected session changes to a different one than the
  // voice is currently bound to, drop the stale binding so the next activation
  // connects to the new guide — and re-activate immediately if mid-call.
  useEffect(() => {
    selectedSessionIdRef.current = selectedSessionId;
    if (
      selectedSessionId &&
      sessionIdRef.current &&
      selectedSessionId !== sessionIdRef.current
    ) {
      const wasActive = isListening || isConnecting;
      stopListening();
      if (wasActive) {
        // Let tearDown's disconnect settle before reconnecting (avoids the
        // WebRTC publish-slot race the tearDown comment describes).
        window.setTimeout(() => {
          startListening().catch(() => {});
        }, 200);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSessionId]);

  return {
    isListening,
    isConnecting,
    hasRemoteParticipant,
    micAudioLevel,
    botAudioLevel,
    isBotWorking,
    permissionState,
    disclaimer,
    toggleListening,
    startListening,
    stopListening,
    setLiveVideoEnabled,
  };
}

export default useAudioChat;
